import type { CSSProperties } from 'react'

import { exponentFor } from '@/lib/money/money'
import { formatMinor } from '../format'
import type { ComposerBlock } from '../spec/types'

/**
 * The instruction composer — the console's only shape for a money-adjacent write.
 *
 * Verbatim from `docs/design/reference/blocks/composer.html`. Four stacked
 * bands on a 2px rule: what is being instructed, the three inputs, the posting
 * preview, and the commit bar with what the audit row will say.
 *
 * Everything about this block is an argument that the operator is composing an
 * INSTRUCTION, not performing an action (law 2):
 *
 *   - The amount is typed in MINOR UNITS with the ISO code welded to its left
 *     and the exponent to its right, and the major-unit reading is a derived
 *     caption underneath — never the field. That ordering is laws 8 and 9 made
 *     physical: there is no box in this console you can type "42.00" into, and
 *     the exponent comes from the ISO-4217 table rather than an assumed two.
 *   - The reason code is a select over the registry, never free text. A
 *     correction whose reason cannot be named from a closed list is one nobody
 *     can review six months from now.
 *   - The idempotency key is shown, copyable, and generated before the
 *     instruction is composed — it is the caller's key, carried unchanged to
 *     the EMI, and redelivery is the normal case rather than the accident.
 *   - Both legs are previewed with the balance line stated, because a
 *     correction is a compensating posting and never an edit (law 3). The
 *     original row this composer corrects stays untouched and visible.
 *
 * The inputs are inert by construction: this renders the design corpus, so
 * there is no local state to diverge from the spec and nothing here submits.
 *
 * ONE DELIBERATE DEVIATION from the slice: the commit button also carries
 * `disabled` when writes are blocked, not merely the dimming attribute. On a
 * standby cell (law 18) or under a role without the scope, a control that
 * looks dead but still takes the click is the exact failure the dimming is
 * there to prevent. `Ver` makes the same call for the same reason.
 */

interface Props {
  readonly block: ComposerBlock
  /** Standby cell, or a role with no write scope on this screen — law 18. */
  readonly writeBlocked: boolean
  /** The screen's one-line explanation of the write posture, shown beneath. */
  readonly writeNote: string
}

export function Composer({ block, writeBlocked, writeNote }: Props) {
  const exp = String(exponentFor(block.cur))
  // The string goes straight through — see formatMinor. No double on the path.
  const preview = formatMinor(block.minor, block.cur)
  // The corpus falls back on falsiness, not on absence: a key note that is
  // present but empty still gets the default sentence rather than a blank
  // line under the field.
  const idemNote =
    block.idemNote !== undefined && block.idemNote !== ''
      ? block.idemNote
      : 'Generated client-side; replay-safe.'

  return (
    <div
      style={{
        border: '1px solid var(--k-line)',
        borderTop: '2px solid var(--k-rule)',
        background: 'var(--k-panel)',
        maxWidth: '900px',
      }}
    >
      <div style={{ padding: '11px 12px', borderBottom: '1px solid var(--k-line)' }}>
        <div style={{ fontSize: '12.5px', fontWeight: 600 }}>{block.lead}</div>
        <div
          style={{
            fontSize: '11.5px',
            color: 'var(--k-mut)',
            marginTop: '3px',
            maxWidth: '78ch',
            textWrap: 'pretty',
          }}
        >
          {block.body}
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '14px',
          alignItems: 'flex-end',
          padding: '11px 12px',
          borderBottom: '1px solid var(--k-line)',
        }}
      >
        <div>
          <div style={FIELD_LABEL}>Amount — minor units</div>
          <div style={{ display: 'flex', alignItems: 'stretch' }}>
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '0 8px',
                border: '1px solid var(--k-line)',
                borderRight: 0,
                background: 'var(--k-panel2)',
                fontFamily: 'var(--k-mono)',
                fontSize: '12px',
                fontWeight: 600,
              }}
            >
              {block.cur}
            </span>
            <input
              value={block.minor}
              readOnly
              inputMode="numeric"
              aria-label="Amount — minor units"
              style={{
                height: '28px',
                width: '130px',
                border: '1px solid var(--k-line)',
                background: 'var(--k-bg)',
                color: 'var(--k-ink)',
                fontFamily: 'var(--k-mono)',
                fontSize: '13px',
                padding: '0 8px',
                textAlign: 'right',
                borderRadius: 0,
              }}
            />
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '0 8px',
                border: '1px solid var(--k-line)',
                borderLeft: 0,
                background: 'var(--k-panel2)',
                fontSize: '11px',
                color: 'var(--k-mut)',
                fontFamily: 'var(--k-mono)',
              }}
            >
              ×10⁻{exp}
            </span>
          </div>
          <div
            style={{
              fontSize: '11px',
              color: 'var(--k-mut)',
              marginTop: '4px',
              fontFamily: 'var(--k-mono)',
            }}
          >
            {`= ${preview} ${block.cur} · integer only, no float path`}
          </div>
        </div>

        <div>
          <div style={FIELD_LABEL}>Reason code — registry</div>
          {/* Uncontrolled and unhandled, exactly as the prototype has it: the
              first registry code shows, and nothing here selects anything. */}
          <select
            aria-label="Reason code — registry"
            style={{
              height: '28px',
              minWidth: '250px',
              border: '1px solid var(--k-line)',
              background: 'var(--k-bg)',
              color: 'var(--k-ink)',
              font: 'inherit',
              fontSize: '12px',
              padding: '0 6px',
              borderRadius: 0,
            }}
          >
            {block.reasons.map((rc) => (
              <option key={rc}>{rc}</option>
            ))}
          </select>
          <div style={FIELD_NOTE}>Select, never free text · note field optional</div>
        </div>

        <div style={{ flex: 1, minWidth: '200px' }}>
          <div style={FIELD_LABEL}>Idempotency key</div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              height: '28px',
              border: '1px solid var(--k-line)',
              background: 'var(--k-panel2)',
              padding: '0 8px',
              fontFamily: 'var(--k-mono)',
              fontSize: '11.5px',
              color: 'var(--k-mut)',
            }}
          >
            <span style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
              {block.idem}
            </span>
            {/* The glyph, not the `CopyGlyph` SVG the tables use — inside a
                mono field the corpus sets the affordance in the same face as
                the key it sits beside. */}
            <span style={{ marginLeft: 'auto', cursor: 'copy' }}>⧉</span>
          </div>
          <div style={FIELD_NOTE}>{idemNote}</div>
        </div>
      </div>

      <div style={{ padding: '11px 12px', borderBottom: '1px solid var(--k-line)' }}>
        <div
          style={{
            fontSize: '10.5px',
            letterSpacing: '.07em',
            textTransform: 'uppercase',
            color: 'var(--k-mut2)',
            marginBottom: '6px',
          }}
        >
          Posting preview — both legs, balanced
        </div>
        <div style={LEG_TRACKS}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'var(--gtc)',
              borderBottom: '2px solid var(--k-rule)',
              paddingBottom: '4px',
              fontSize: '9.5px',
              letterSpacing: '.07em',
              textTransform: 'uppercase',
              color: 'var(--k-mut)',
            }}
          >
            <span>Account</span>
            <span>Class</span>
            <span data-a="r">Debit</span>
            <span data-a="r">Credit</span>
          </div>
          {block.legs.map((leg, i) => {
            // A leg has no id of its own; account plus its position in the
            // pair identifies it, and the pair is fixed by the spec.
            const key = `${leg[0]}·${String(i)}`
            // A zero side of a leg is an em-dash, never "0.00": a debit-only
            // leg has no credit, which is a different statement from a credit
            // of nothing. Both non-zero sides go through `formatMinor` with
            // the composer's currency shown beside them (law 8).
            const debit = leg[2] === 0 ? '—' : formatMinor(leg[2], block.cur)
            const credit = leg[3] === 0 ? '—' : formatMinor(leg[3], block.cur)
            return (
              <div
                key={key}
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'var(--gtc)',
                  alignItems: 'center',
                  borderBottom: '1px solid var(--k-line)',
                  padding: '6px 0',
                  fontSize: '12px',
                }}
              >
                <span style={{ fontFamily: 'var(--k-mono)', fontSize: '11.5px' }}>{leg[0]}</span>
                <span style={{ color: 'var(--k-mut)', fontSize: '11.5px' }}>{leg[1]}</span>
                <span data-a="r" style={LEG_AMOUNT}>
                  {debit}
                  <span style={LEG_CUR}>{leg[2] === 0 ? '' : block.cur}</span>
                </span>
                <span data-a="r" style={LEG_AMOUNT}>
                  {credit}
                  <span style={LEG_CUR}>{leg[3] === 0 ? '' : block.cur}</span>
                </span>
              </div>
            )
          })}
        </div>
        <div style={{ display: 'flex', gap: '14px', marginTop: '8px', fontSize: '11.5px' }}>
          <span style={{ fontFamily: 'var(--k-mono)' }}>{block.balanceLine}</span>
          <span style={{ color: 'var(--k-mut)' }}>{block.deltaLine}</span>
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          flexWrap: 'wrap',
          padding: '11px 12px',
          background: 'var(--k-bg)',
        }}
      >
        <button
          type="button"
          disabled={writeBlocked}
          data-dim={writeBlocked ? '1' : ''}
          style={{
            height: '29px',
            padding: '0 13px',
            border: 0,
            background: 'var(--k-accent)',
            color: 'var(--k-accentink)',
            font: 'inherit',
            fontWeight: 800,
            fontSize: '12px',
            cursor: 'pointer',
          }}
        >
          {block.cta}
        </button>
        <span style={{ fontSize: '11.5px', color: 'var(--k-mut)', maxWidth: '60ch' }}>
          {block.ctaNote}
        </span>
        {/* Stated before the click, not discovered in the audit log after it:
            an audit row is written for this instruction or it does not happen
            (invariant 6). */}
        <span
          style={{
            marginLeft: 'auto',
            fontSize: '11px',
            color: 'var(--k-mut2)',
            fontFamily: 'var(--k-mono)',
          }}
        >
          will be recorded: actor · action · subject · reason_code
        </span>
      </div>

      <div style={{ padding: '0 12px 11px', fontSize: '11px', color: 'var(--k-mut2)' }}>
        {writeNote}
      </div>
    </div>
  )
}

const FIELD_LABEL = {
  fontSize: '10px',
  letterSpacing: '.06em',
  textTransform: 'uppercase',
  color: 'var(--k-mut2)',
  marginBottom: '4px',
} as const

const FIELD_NOTE = {
  fontSize: '11px',
  color: 'var(--k-mut)',
  marginTop: '4px',
} as const

/** The legs table's own grid tracks, carried as the corpus's `--gtc`. */
const LEG_TRACKS = { '--gtc': '1.5fr 1fr .8fr .8fr' } as CSSProperties

const LEG_AMOUNT = {
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  fontWeight: 600,
} as const

const LEG_CUR = {
  fontFamily: 'var(--k-mono)',
  fontSize: '9.5px',
  color: 'var(--k-mut)',
} as const
